/**
 * Deployments View — Releases + Deploy + Sessions in one place.
 */

import React, { memo, useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import API from "Utils/api";
import {
  OTADeploymentSession,
  OTADeploymentTarget,
  OTADeploymentTargetComponent,
  StateYamlReport,
} from "../../types";
import { deploymentSessionsApi } from "../../api";
import { CloudDeploymentWizard } from "../OTAInstallationView/CloudDeploymentWizard";
import { ReleaseWizard } from "../ReleaseWizard";
import { ImportReleaseContent } from "../ImportReleaseModal";
import { DeleteConfirmationModal } from "../DeleteConfirmationModal";

// ── Local types ───────────────────────────────────────────────────────────────

interface Release {
  id: string;
  name: string;
  vehicleTypeName: string;
  vehicleTypeModel: string;
  description: string;
  orchestratorVersion: string;
  status: string;
  componentCount: number;
  ccmAutonomy: string;
  ccmConfig: string;
  createdAt: string;
}

interface ReleaseComponent {
  id: string;
  componentName: string;
  componentType: string;
  awsComponentName: string;
  versionNumber: string;
  ccmImageTag: string | null;
  ccmServiceName: string | null;
  ccmSourceName: string | null;
  s3Uri: string | null;
  sha256Hash: string | null;
  firmwareVersion: string | null;
  hostPackageVersion: string | null;
  description: string | null;
  tags: string[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function unique(arr: string[]): string[] {
  return Array.from(new Set(arr.filter(Boolean)));
}

const statusColors: Record<string, string> = {
  PENDING: "#ff9800",
  IN_PROGRESS: "#2196f3",
  QUEUED: "#9c27b0",
  DOWNLOADING: "#03a9f4",
  READY_FOR_INSTALLATION: "#8bc34a",
  INSTALLING: "#ff5722",
  COMPLETED: "#4caf50",
  FAILED: "#f44336",
  CANCELLED: "#9e9e9e",
  TIMED_OUT: "#e91e63",
  SKIPPED: "#78909c",
  DOWNLOADED: "#26c6da",
  INSTALLED: "#4caf50",
};

const statusLabel: Record<string, string> = {
  PENDING: "Pending",
  IN_PROGRESS: "In Progress",
  QUEUED: "Queued",
  DOWNLOADING: "Downloading",
  READY_FOR_INSTALLATION: "Ready for Installation",
  INSTALLING: "Installing",
  COMPLETED: "Completed",
  FAILED: "Failed",
  CANCELLED: "Cancelled",
  TIMED_OUT: "Timed Out",
  SKIPPED: "Skipped",
  DOWNLOADED: "Downloaded",
  INSTALLED: "Installed",
};

const releaseStatusColor = (status: string) => {
  switch (status) {
    case "ready":
      return "#4caf50";
    case "deployed":
      return "#2196f3";
    case "deprecated":
      return "#9e9e9e";
    default:
      return "#ff9800";
  }
};

const progressColor = (pct: number) => {
  if (pct === 100) return "#4caf50";
  if (pct >= 50) return "#2196f3";
  return "#55b8b3";
};

const formatDate = (iso: string | null) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
};

const STATUS_OPTIONS: Array<{ label: string; value: string }> = [
  { label: "All statuses", value: "" },
  { label: "Pending", value: "PENDING" },
  { label: "In progress", value: "IN_PROGRESS" },
  { label: "Completed", value: "COMPLETED" },
  { label: "Failed", value: "FAILED" },
  { label: "Cancelled", value: "CANCELLED" },
];

// ── Styled components ─────────────────────────────────────────────────────────

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FilterLabel = styled.span`
  font-size: 13px;
  color: #606060;
  white-space: nowrap;
`;

const FilterSelect = styled.select`
  font-family: "Readex Pro", sans-serif;
  font-size: 13px;
  padding: 5px 10px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  color: #172a3f;
  background: #ffffff;
  cursor: pointer;
`;

const RefreshButton = styled.button`
  font-family: "Readex Pro", sans-serif;
  font-size: 13px;
  color: #55b8b3;
  background: transparent;
  border: 1px solid #55b8b3;
  border-radius: 6px;
  padding: 6px 14px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #f0fafa;
  }
`;

const SectionDivider = styled.div`
  border-top: 2px solid #e8e8e8;
  margin: 8px 0;
`;

// ── Releases table ─────────────────────────────────────────────────────────────

const ReleasesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.tr`
  background: #f8f9fa;
  border-bottom: 2px solid #e0e0e0;
`;

const TableHeaderCell = styled.th`
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #666;
  text-align: left;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e0e0e0;
  &:hover {
    background: #f8f9fa;
  }
`;

const TableCell = styled.td`
  padding: 12px;
  font-size: 13px;
  color: #333;
`;

const ReleaseStatusBadge = styled.span<{ color: string }>`
  display: inline-block;
  padding: 3px 10px;
  background: ${({ color }) => color}20;
  color: ${({ color }) => color};
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  text-transform: capitalize;
`;

const DeleteBtn = styled.button`
  padding: 4px 12px;
  background: white;
  color: #d32f2f;
  border: 1px solid #d32f2f;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #d32f2f;
    color: white;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// ── Session / target / component ─────────────────────────────────────────────

const BrickedBanner = styled.div`
  margin: 6px 0 8px;
  padding: 8px 12px;
  background: #9c27b015;
  border: 1px solid #9c27b060;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #7b1fa2;
`;

const HostStatusBadge = styled.span<{ $status: string }>`
  padding: 1px 6px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 600;
  background: ${({ $status }) =>
    $status === "ok"
      ? "#4caf5020"
      : $status === "rollback_failed"
      ? "#9c27b020"
      : "#f4433620"};
  color: ${({ $status }) =>
    $status === "ok"
      ? "#4caf50"
      : $status === "rollback_failed"
      ? "#9c27b0"
      : "#f44336"};
`;

const Badge = styled.span<{ status: string }>`
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 10px;
  color: #fff;
  background: ${({ status }) => statusColors[status] ?? "#9e9e9e"};
  white-space: nowrap;
`;

const ProgressBarOuter = styled.div`
  width: 120px;
  height: 6px;
  background: #e8e8e8;
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div<{ pct: number; color: string }>`
  height: 100%;
  width: ${({ pct }) => pct}%;
  background: ${({ color }) => color};
  border-radius: 3px;
  transition: width 0.3s ease;
`;

const ProgressText = styled.span`
  font-size: 11px;
  color: #888;
`;

const ExpandIcon = styled.span<{ expanded: boolean }>`
  font-size: 12px;
  color: #888;
  transform: ${({ expanded }) =>
    expanded ? "rotate(180deg)" : "rotate(0deg)"};
  display: inline-block;
  transition: transform 0.2s ease;
`;

const SessionCard = styled.div`
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
`;

const SessionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e8e8e8;
  cursor: pointer;
  &:hover {
    background: #f0fafa;
  }
`;

const SessionMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SessionName = styled.span`
  font-family: "Readex Pro", sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: #172a3f;
`;

const SessionSubText = styled.span`
  font-size: 12px;
  color: #888;
`;

const SessionRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const OverallProgress = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const TargetList = styled.div`
  padding: 12px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TargetRow = styled.div`
  border: 1px solid #eee;
  border-radius: 6px;
  overflow: hidden;
`;

const TargetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  cursor: pointer;
  background: #fafafa;
  &:hover {
    background: #f0fafa;
  }
`;

const TargetVin = styled.span`
  font-family: "Readex Pro", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #172a3f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
`;

const TargetRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ComponentList = styled.div`
  padding: 8px 14px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ComponentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px;
  background: #f8f9fa;
  border-radius: 4px;
`;

const ComponentName = styled.span`
  font-size: 12px;
  color: #444;
`;
const ComponentTime = styled.span`
  font-size: 11px;
  color: #aaa;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 24px;
  color: #888;
  font-size: 14px;
`;

const InlineEmpty = styled.div`
  padding: 16px 20px;
  color: #aaa;
  font-size: 13px;
  text-align: center;
`;

const InlineLoading = styled.div`
  padding: 16px 20px;
  color: #888;
  font-size: 13px;
  text-align: center;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 24px;
  color: #888;
  font-size: 14px;
`;

// ── Release detail modal ──────────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  max-width: 900px;
  width: 92vw;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 18px 24px 14px;
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const ModalTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
`;

const ModalMeta = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  display: flex;
  gap: 16px;
`;

const ModalCloseBtn = styled.button`
  border: none;
  background: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  line-height: 1;
  padding: 2px 6px;
  border-radius: 4px;
  &:hover {
    background: #f0f0f0;
    color: #333;
  }
`;

const ModalBody = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const ComponentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  padding: 16px 24px;
`;

const ComponentCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const CardName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CardVersion = styled.span`
  font-size: 13px;
  color: #0369a1;
  font-weight: 500;
  white-space: nowrap;
`;

const TypeBadge = styled.span<{ $type: string }>`
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 4px;
  white-space: nowrap;
  background: ${({ $type }) =>
    $type === "container"
      ? "#dbeafe"
      : $type === "host"
      ? "#d1fae5"
      : "#fef3c7"};
  color: ${({ $type }) =>
    $type === "container"
      ? "#1e40af"
      : $type === "host"
      ? "#065f46"
      : "#92400e"};
`;

const CardBody = styled.div`
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const VDSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const VDSectionTitle = styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #9ca3af;
  padding-bottom: 4px;
  border-bottom: 1px solid #f3f4f6;
`;

const VDRow = styled.div`
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 6px;
  align-items: baseline;
`;

const VDLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  flex-shrink: 0;
`;

const VDValue = styled.span`
  font-size: 12px;
  color: #111827;
  word-break: break-all;

  code {
    background: #f3f4f6;
    padding: 1px 5px;
    border-radius: 3px;
    font-size: 11px;
  }
`;

const ReleaseLinkBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #1976d2;
  font-weight: 600;
  font-size: inherit;
  text-align: left;
  text-decoration: underline;
  text-decoration-color: transparent;
  &:hover {
    text-decoration-color: #1976d2;
  }
`;

// ── Two-column layout ─────────────────────────────────────────────────────────

const TwoColRow = styled.div`
  display: grid;
  grid-template-columns: minmax(360px, 42%) 1fr;
  gap: 24px;
  align-items: start;
`;

const WizardPanel = styled.div`
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
`;

const ListPanel = styled.div`
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  display: flex;
  flex-direction: column;
`;

const ListPanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 12px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #e8e8e8;
`;

const ListPanelTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #172a3f;
  flex-shrink: 0;
`;

const ListPanelBody = styled.div<{ $expanded: boolean }>`
  padding: 14px 16px 24px;
  overflow-y: ${({ $expanded }) => ($expanded ? "visible" : "auto")};
  max-height: ${({ $expanded }) => ($expanded ? "none" : "480px")};
`;

const ExpandToggle = styled.button`
  width: 100%;
  padding: 7px 16px;
  background: #f8f9fa;
  border: none;
  border-top: 1px solid #e8e8e8;
  font-size: 12px;
  color: #55b8b3;
  cursor: pointer;
  text-align: center;

  &:hover {
    background: #f0fafa;
    color: #3d9e9a;
  }
`;

// ── Sub-components ─────────────────────────────────────────────────────────────

const ComponentDetail = memo<{ comp: OTADeploymentTargetComponent }>(
  ({ comp }) => (
    <ComponentRow>
      <ComponentName>{comp.componentName}</ComponentName>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <ComponentTime>v{comp.versionNumber}</ComponentTime>
        <Badge status={comp.status}>
          {statusLabel[comp.status] ?? comp.status}
        </Badge>
        {comp.status === "DOWNLOADED" && comp.downloadedAt && (
          <ComponentTime>{formatDate(comp.downloadedAt)}</ComponentTime>
        )}
        {comp.status === "INSTALLED" && comp.installedAt && (
          <ComponentTime>{formatDate(comp.installedAt)}</ComponentTime>
        )}
        {comp.errorMessage && (
          <ComponentTime style={{ color: "#f44336" }}>
            {comp.errorMessage}
          </ComponentTime>
        )}
      </div>
    </ComponentRow>
  )
);

const TargetDetail = memo<{
  target: OTADeploymentTarget;
}>(({ target }) => {
  const [expanded, setExpanded] = useState(false);
  const pct = target.progress ?? 0;
  const total = target.totalComponents ?? 0;
  const done = target.downloadedComponents ?? 0;

  return (
    <TargetRow>
      <TargetHeader onClick={() => setExpanded((v) => !v)}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minWidth: 0,
            marginRight: 12,
          }}
        >
          <TargetVin title={target.vehicleThingName}>
            {target.vehicleThingName}
          </TargetVin>
          {target.vehicleVin && (
            <span style={{ fontSize: 11, color: "#aaa" }}>
              VIN: {target.vehicleVin}
            </span>
          )}
        </div>
        <TargetRight>
          {total > 0 && (
            <ProgressText>
              {done}/{total} components
            </ProgressText>
          )}
          <ProgressBarOuter>
            <ProgressBarFill pct={pct} color={progressColor(pct)} />
          </ProgressBarOuter>
          <Badge status={target.status}>
            {statusLabel[target.status] ?? target.status}
          </Badge>
          <ExpandIcon expanded={expanded}>▼</ExpandIcon>
        </TargetRight>
      </TargetHeader>

      {target.errorMessage && (
        <div
          style={{
            padding: "5px 14px 7px",
            fontSize: 12,
            color: "#ef4444",
            borderTop: "1px solid #eee",
          }}
        >
          {target.errorMessage}
        </div>
      )}

      {expanded && (
        <>
          {target.components && target.components.length > 0 ? (
            <ComponentList>
              {target.components.map((c) => (
                <ComponentDetail key={c.id} comp={c} />
              ))}
            </ComponentList>
          ) : (
            <InlineEmpty>No components found for this vehicle.</InlineEmpty>
          )}
        </>
      )}
      {target.firmwareReport &&
        Object.keys(target.firmwareReport).length > 0 && (
          <div
            style={{ padding: "8px 14px 12px", borderTop: "1px solid #eee" }}
          >
            <div style={{ fontSize: 11, color: "#aaa", marginBottom: 4 }}>
              Vehicle report
              {target.firmwareReportAt && (
                <span style={{ marginLeft: 8 }}>
                  · {formatDate(target.firmwareReportAt)}
                </span>
              )}
              {target.firmwareOverallStatus && (
                <span
                  style={{
                    marginLeft: 8,
                    padding: "1px 7px",
                    borderRadius: 8,
                    fontSize: 10,
                    fontWeight: 600,
                    background:
                      target.firmwareOverallStatus === "ok" ||
                      target.firmwareOverallStatus === "success"
                        ? "#4caf5020"
                        : target.firmwareOverallStatus === "bricked"
                        ? "#9c27b020"
                        : "#f4433620",
                    color:
                      target.firmwareOverallStatus === "ok" ||
                      target.firmwareOverallStatus === "success"
                        ? "#4caf50"
                        : target.firmwareOverallStatus === "bricked"
                        ? "#9c27b0"
                        : "#f44336",
                  }}
                >
                  {target.firmwareOverallStatus.toUpperCase()}
                </span>
              )}
            </div>
            {target.firmwareOverallStatus === "bricked" && (
              <BrickedBanner>
                ⚠ BRICKED — firmware failed and host rollback failed. Manual
                recovery required.
              </BrickedBanner>
            )}
            {(() => {
              const report =
                target.firmwareReport as unknown as StateYamlReport;
              const hostStatus = report?.component_status?.host;
              if (!hostStatus || Object.keys(hostStatus).length === 0)
                return null;
              return (
                <div style={{ marginBottom: 8 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#666",
                      marginBottom: 4,
                    }}
                  >
                    Host components
                  </div>
                  {Object.entries(hostStatus).map(([name, info]) => (
                    <div
                      key={name}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontSize: 11,
                        marginBottom: 3,
                      }}
                    >
                      <span style={{ fontFamily: "monospace", color: "#333" }}>
                        {name}
                      </span>
                      <HostStatusBadge $status={info.status}>
                        {info.status}
                      </HostStatusBadge>
                      {info.current && (
                        <span style={{ color: "#888" }}>
                          current: {info.current}
                        </span>
                      )}
                      {info.expected && info.expected !== info.current && (
                        <span style={{ color: "#888" }}>
                          expected: {info.expected}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
            {(() => {
              const report =
                target.firmwareReport as unknown as StateYamlReport;
              const mismatched = report?.mismatched_components;
              if (!mismatched || mismatched.length === 0) return null;
              return (
                <div style={{ marginBottom: 8 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#666",
                      marginBottom: 4,
                    }}
                  >
                    Mismatched components
                  </div>
                  {mismatched.map((m, i) => (
                    <div
                      key={i}
                      style={{
                        fontSize: 11,
                        color: "#f44336",
                        marginBottom: 2,
                      }}
                    >
                      {m.component}: expected {m.expected ?? "?"}, got{" "}
                      {m.actual ?? "?"}
                    </div>
                  ))}
                </div>
              );
            })()}
            <details>
              <summary
                style={{
                  fontSize: 11,
                  color: "#aaa",
                  cursor: "pointer",
                  marginBottom: 4,
                }}
              >
                Raw report
              </summary>
              <pre
                style={{
                  fontSize: 11,
                  background: "#f8f9fa",
                  border: "1px solid #e8e8e8",
                  borderRadius: 4,
                  padding: "8px 10px",
                  margin: 0,
                  overflowX: "auto",
                  maxHeight: 200,
                  color: "#333",
                }}
              >
                {JSON.stringify(target.firmwareReport, null, 2)}
              </pre>
            </details>
          </div>
        )}
    </TargetRow>
  );
});

const SessionDetail = memo<{
  session: OTADeploymentSession;
  onDelete?: () => void;
}>(({ session, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState<OTADeploymentSession | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const TERMINAL = ["COMPLETED", "FAILED", "CANCELLED", "TIMED_OUT"];
  const isTerminal = TERMINAL.includes((detail ?? session).status ?? "");

  const handleExpand = useCallback(async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !detail) {
      setDetailLoading(true);
      try {
        const data = await deploymentSessionsApi.getById(session.id);
        setDetail(data);
      } catch {
        toast.error("Failed to load session details");
      } finally {
        setDetailLoading(false);
      }
    }
  }, [expanded, detail, session.id]);

  useEffect(() => {
    if (!expanded || isTerminal) return;
    const id = setInterval(async () => {
      try {
        const data = await deploymentSessionsApi.getById(session.id);
        setDetail(data);
      } catch {
        // silent — stale data is fine
      }
    }, 10_000);
    return () => clearInterval(id);
  }, [expanded, isTerminal, session.id]);

  const active = detail ?? session;
  const pct = active.overallProgress ?? 0;
  const total = active.totalTargets ?? 0;
  const completed = active.completedTargets ?? 0;
  const failed = active.failedTargets ?? 0;

  return (
    <SessionCard>
      <SessionHeader onClick={handleExpand}>
        <SessionMeta>
          <SessionName>{active.releaseName}</SessionName>
          <SessionSubText>
            Fleet: {active.fleetName} &nbsp;·&nbsp; Vehicle type:{" "}
            {active.vehicleTypeName} &nbsp;·&nbsp; Strategy:{" "}
            {active.deploymentStrategy}
          </SessionSubText>
          {total > 0 && (
            <SessionSubText>
              {completed} completed · {failed} failed · {total} total vehicles
            </SessionSubText>
          )}
        </SessionMeta>
        <SessionRight>
          <OverallProgress>
            <ProgressBarOuter style={{ width: 150 }}>
              <ProgressBarFill pct={pct} color={progressColor(pct)} />
            </ProgressBarOuter>
            <ProgressText>{pct}%</ProgressText>
          </OverallProgress>
          <Badge status={active.status}>
            {statusLabel[active.status] ?? active.status}
          </Badge>
          {onDelete && (
            <DeleteBtn
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              title="Delete deployment session"
            >
              Delete
            </DeleteBtn>
          )}
          <ExpandIcon expanded={expanded}>▼</ExpandIcon>
        </SessionRight>
      </SessionHeader>

      {expanded &&
        (detailLoading ? (
          <InlineLoading>Loading vehicles…</InlineLoading>
        ) : detail?.targets && detail.targets.length > 0 ? (
          <TargetList>
            {detail.targets.map((t) => (
              <TargetDetail key={t.id} target={t} />
            ))}
          </TargetList>
        ) : detail ? (
          <InlineEmpty>No vehicles found in this deployment.</InlineEmpty>
        ) : null)}
    </SessionCard>
  );
});

// ── Main component ─────────────────────────────────────────────────────────────

export const DeploymentsView = memo(() => {
  // ── Releases ────────────────────────────────────────────────────────────────
  const [releases, setReleases] = useState<Release[]>([]);
  const [releasesLoading, setReleasesLoading] = useState(false);
  const [releaseVTFilter, setReleaseVTFilter] = useState("");
  const [releaseWizardKey, setReleaseWizardKey] = useState(0);
  const [importContentKey, setImportContentKey] = useState(0);
  const [showAdvancedWizard, setShowAdvancedWizard] = useState(false);
  const [releasesExpanded, setReleasesExpanded] = useState(false);
  const [releaseDetail, setReleaseDetail] = useState<{
    release: Release;
    components: ReleaseComponent[];
  } | null>(null);
  const [releaseDetailLoading, setReleaseDetailLoading] = useState(false);

  const handleReleaseNameClick = useCallback(
    async (e: React.MouseEvent, release: Release) => {
      e.stopPropagation();
      setReleaseDetail({ release, components: [] });
      setReleaseDetailLoading(true);
      try {
        const res = await API.get(`/ota/releases/${release.id}/`);
        setReleaseDetail({ release, components: res.data?.components ?? [] });
      } catch {
        toast.error("Failed to load release components");
        setReleaseDetail(null);
      } finally {
        setReleaseDetailLoading(false);
      }
    },
    []
  );

  // ── Sessions ─────────────────────────────────────────────────────────────────
  const [sessions, setSessions] = useState<OTADeploymentSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionStatusFilter, setSessionStatusFilter] = useState("");
  const [sessionVTFilter, setSessionVTFilter] = useState("");
  const [sessionFleetFilter, setSessionFleetFilter] = useState("");
  const [sessionsExpanded, setSessionsExpanded] = useState(false);
  const [pendingDeleteRelease, setPendingDeleteRelease] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [pendingDeleteSession, setPendingDeleteSession] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const fetchReleases = useCallback(async () => {
    setReleasesLoading(true);
    try {
      const res = await API.get("/ota/releases/");
      setReleases(res.data?.results || res.data || []);
    } catch {
      toast.error("Failed to load releases");
    } finally {
      setReleasesLoading(false);
    }
  }, []);

  const fetchSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const data = await deploymentSessionsApi.getAll(
        sessionStatusFilter ? { status: sessionStatusFilter } : undefined
      );
      setSessions(data);
    } catch {
      toast.error("Failed to load deployment sessions");
    } finally {
      setSessionsLoading(false);
    }
  }, [sessionStatusFilter]);

  useEffect(() => {
    fetchReleases();
  }, [fetchReleases]);
  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleDeleteSession = (id: string, name: string) => {
    setPendingDeleteSession({ id, name });
  };

  const confirmDeleteSession = async () => {
    if (!pendingDeleteSession) return;
    const { id } = pendingDeleteSession;
    setPendingDeleteSession(null);
    try {
      await deploymentSessionsApi.delete(id);
      toast.success("Deployment session deleted");
      fetchSessions();
    } catch (err: any) {
      toast.error(
        err.response?.data?.message || "Failed to delete deployment session"
      );
    }
  };

  const handleDeleteRelease = (id: string, name: string) => {
    setPendingDeleteRelease({ id, name });
  };

  const confirmDeleteRelease = async () => {
    if (!pendingDeleteRelease) return;
    const { id } = pendingDeleteRelease;
    setPendingDeleteRelease(null);
    try {
      await API.delete(`/ota/releases/${id}/`);
      toast.success("Release deleted");
      fetchReleases();
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to delete release");
    }
  };

  // Derived filter options (extracted from loaded data — no extra API calls)
  const releaseVTOptions = unique(releases.map((r) => r.vehicleTypeName));
  const sessionVTOptions = unique(sessions.map((s) => s.vehicleTypeName));
  const sessionFleetOptions = unique(sessions.map((s) => s.fleetName));

  // Filtered data
  const filteredReleases = releaseVTFilter
    ? releases.filter((r) => r.vehicleTypeName === releaseVTFilter)
    : releases;

  const filteredSessions = sessions.filter((s) => {
    if (sessionVTFilter && s.vehicleTypeName !== sessionVTFilter) return false;
    if (sessionFleetFilter && s.fleetName !== sessionFleetFilter) return false;
    return true;
  });

  return (
    <Container>
      {/* ── Top row: Release Wizard (left) + Releases list (right) ───────────── */}
      <TwoColRow>
        <WizardPanel>
          {showAdvancedWizard ? (
            <ReleaseWizard
              key={releaseWizardKey}
              onClose={() => {
                setReleaseWizardKey((k) => k + 1);
                setShowAdvancedWizard(false);
              }}
              onSuccess={() => {
                fetchReleases();
                setReleaseWizardKey((k) => k + 1);
                setShowAdvancedWizard(false);
              }}
            />
          ) : (
            <ImportReleaseContent
              key={importContentKey}
              onSuccess={() => {
                fetchReleases();
                setImportContentKey((k) => k + 1);
              }}
            />
          )}
        </WizardPanel>

        <ListPanel>
          <ListPanelHeader>
            <ListPanelTitle>
              Releases ({filteredReleases.length})
            </ListPanelTitle>
            {releases.length > 0 && (
              <>
                <FilterLabel>Vehicle Type:</FilterLabel>
                <FilterSelect
                  value={releaseVTFilter}
                  onChange={(e) => setReleaseVTFilter(e.target.value)}
                >
                  <option value="">All</option>
                  {releaseVTOptions.map((vt) => (
                    <option key={vt} value={vt}>
                      {vt}
                    </option>
                  ))}
                </FilterSelect>
              </>
            )}
          </ListPanelHeader>
          <ListPanelBody $expanded={releasesExpanded}>
            {releasesLoading ? (
              <LoadingState>Loading releases…</LoadingState>
            ) : filteredReleases.length === 0 ? (
              <EmptyState>
                {releases.length === 0
                  ? "No releases yet — fill the wizard to create one."
                  : "No releases match the selected filter."}
              </EmptyState>
            ) : (
              <ReleasesTable>
                <thead>
                  <TableHeader>
                    <TableHeaderCell>Name</TableHeaderCell>
                    <TableHeaderCell>Vehicle Type</TableHeaderCell>
                    <TableHeaderCell>Components</TableHeaderCell>
                    <TableHeaderCell>Orchestrator</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Created</TableHeaderCell>
                    <TableHeaderCell></TableHeaderCell>
                  </TableHeader>
                </thead>
                <tbody>
                  {filteredReleases.map((release) => (
                    <TableRow key={release.id}>
                      <TableCell>
                        <ReleaseLinkBtn
                          onClick={(e) => handleReleaseNameClick(e, release)}
                        >
                          {release.name}
                        </ReleaseLinkBtn>
                        {release.description && (
                          <div
                            style={{
                              fontSize: 11,
                              color: "#666",
                              marginTop: 3,
                            }}
                          >
                            {release.description.substring(0, 60)}
                            {release.description.length > 60 ? "…" : ""}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {release.vehicleTypeName}
                        {release.vehicleTypeModel && (
                          <div style={{ fontSize: 11, color: "#666" }}>
                            {release.vehicleTypeModel}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{release.componentCount}</TableCell>
                      <TableCell>
                        {release.orchestratorVersion ? (
                          `v${release.orchestratorVersion}`
                        ) : (
                          <span style={{ color: "#ff9800" }}>Not synced</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <ReleaseStatusBadge
                          color={releaseStatusColor(release.status)}
                        >
                          {release.status}
                        </ReleaseStatusBadge>
                      </TableCell>
                      <TableCell>
                        {new Date(release.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <DeleteBtn
                          onClick={() =>
                            handleDeleteRelease(release.id, release.name)
                          }
                          disabled={release.status === "deployed"}
                          title={
                            release.status === "deployed"
                              ? "Cannot delete a deployed release"
                              : ""
                          }
                        >
                          Delete
                        </DeleteBtn>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </ReleasesTable>
            )}
          </ListPanelBody>
          <ExpandToggle onClick={() => setReleasesExpanded((v) => !v)}>
            {releasesExpanded ? "▲ Collapse" : "▼ Show all"}
          </ExpandToggle>
        </ListPanel>
      </TwoColRow>

      <SectionDivider />

      {/* ── Bottom row: Deployment Wizard (left) + Sessions list (right) ──────── */}
      <TwoColRow>
        <WizardPanel>
          <CloudDeploymentWizard onDeploySuccess={fetchSessions} />
        </WizardPanel>

        <ListPanel>
          <ListPanelHeader>
            <ListPanelTitle>
              Deployment Sessions ({filteredSessions.length})
            </ListPanelTitle>
            {sessionVTOptions.length > 0 && (
              <>
                <FilterLabel>Vehicle Type:</FilterLabel>
                <FilterSelect
                  value={sessionVTFilter}
                  onChange={(e) => setSessionVTFilter(e.target.value)}
                >
                  <option value="">All</option>
                  {sessionVTOptions.map((vt) => (
                    <option key={vt} value={vt}>
                      {vt}
                    </option>
                  ))}
                </FilterSelect>
              </>
            )}
            {sessionFleetOptions.length > 0 && (
              <>
                <FilterLabel>Fleet:</FilterLabel>
                <FilterSelect
                  value={sessionFleetFilter}
                  onChange={(e) => setSessionFleetFilter(e.target.value)}
                >
                  <option value="">All</option>
                  {sessionFleetOptions.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </FilterSelect>
              </>
            )}
            <FilterLabel>Status:</FilterLabel>
            <FilterSelect
              value={sessionStatusFilter}
              onChange={(e) => setSessionStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </FilterSelect>
            <RefreshButton onClick={fetchSessions}>↻ Refresh</RefreshButton>
          </ListPanelHeader>
          <ListPanelBody $expanded={sessionsExpanded}>
            {sessionsLoading ? (
              <LoadingState>Loading deployment sessions…</LoadingState>
            ) : filteredSessions.length === 0 ? (
              <EmptyState>
                {sessions.length === 0
                  ? "No deployment sessions yet."
                  : "No sessions match the selected filters."}
              </EmptyState>
            ) : (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                {filteredSessions.map((s) => (
                  <SessionDetail
                    key={s.id}
                    session={s}
                    onDelete={() => handleDeleteSession(s.id, s.releaseName)}
                  />
                ))}
              </div>
            )}
          </ListPanelBody>
          <ExpandToggle onClick={() => setSessionsExpanded((v) => !v)}>
            {sessionsExpanded ? "▲ Collapse" : "▼ Show all"}
          </ExpandToggle>
        </ListPanel>
      </TwoColRow>
      <DeleteConfirmationModal
        isOpen={!!pendingDeleteRelease}
        entityName={pendingDeleteRelease?.name ?? ""}
        entityType="Release"
        onConfirm={confirmDeleteRelease}
        onClose={() => setPendingDeleteRelease(null)}
      />

      <DeleteConfirmationModal
        isOpen={!!pendingDeleteSession}
        entityName={pendingDeleteSession?.name ?? ""}
        entityType="Deployment Session"
        warningMessage="This will cancel any active AWS deployments and permanently remove all records for this session."
        onConfirm={confirmDeleteSession}
        onClose={() => setPendingDeleteSession(null)}
      />

      {releaseDetail && (
        <ModalOverlay onClick={() => setReleaseDetail(null)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <div>
                <ModalTitle>{releaseDetail.release.name}</ModalTitle>
                <ModalMeta>
                  <span>
                    <strong>Vehicle type:</strong>{" "}
                    {releaseDetail.release.vehicleTypeName}
                  </span>
                  <span>
                    <strong>Autonomy:</strong>{" "}
                    {releaseDetail.release.ccmAutonomy}
                  </span>
                  <span>
                    <strong>Config:</strong> {releaseDetail.release.ccmConfig}
                  </span>
                  <span>
                    <ReleaseStatusBadge
                      color={releaseStatusColor(releaseDetail.release.status)}
                    >
                      {releaseDetail.release.status}
                    </ReleaseStatusBadge>
                  </span>
                </ModalMeta>
              </div>
              <ModalCloseBtn onClick={() => setReleaseDetail(null)}>
                ×
              </ModalCloseBtn>
            </ModalHeader>
            <ModalBody>
              {releaseDetailLoading ? (
                <InlineLoading>Loading components…</InlineLoading>
              ) : releaseDetail.components.length === 0 ? (
                <InlineEmpty>No components in this release.</InlineEmpty>
              ) : (
                <ComponentsGrid>
                  {releaseDetail.components.map((c) => {
                    const type = c.componentType.toLowerCase();
                    const isContainer = type === "container";
                    const isHost = type === "host";
                    const isFirmware = !isContainer && !isHost;
                    return (
                      <ComponentCard key={c.id}>
                        <CardHeader>
                          <CardName title={c.componentName}>
                            {c.componentName}
                          </CardName>
                          <CardVersion>v{c.versionNumber}</CardVersion>
                          <TypeBadge $type={type}>{c.componentType}</TypeBadge>
                        </CardHeader>
                        <CardBody>
                          <VDSection>
                            <VDSectionTitle>Identification</VDSectionTitle>
                            <VDRow>
                              <VDLabel>Version</VDLabel>
                              <VDValue>{c.versionNumber}</VDValue>
                            </VDRow>
                            <VDRow>
                              <VDLabel>AWS Component</VDLabel>
                              <VDValue>
                                <code>{c.awsComponentName || "—"}</code>
                              </VDValue>
                            </VDRow>
                          </VDSection>

                          {isContainer && (
                            <VDSection>
                              <VDSectionTitle>CCM Configuration</VDSectionTitle>
                              <VDRow>
                                <VDLabel>Image Tag</VDLabel>
                                <VDValue>
                                  <code>{c.ccmImageTag || "—"}</code>
                                </VDValue>
                              </VDRow>
                              {c.ccmServiceName && (
                                <VDRow>
                                  <VDLabel>Service Name</VDLabel>
                                  <VDValue>
                                    <code>{c.ccmServiceName}</code>
                                  </VDValue>
                                </VDRow>
                              )}
                              {c.ccmSourceName && (
                                <VDRow>
                                  <VDLabel>Source Name</VDLabel>
                                  <VDValue>
                                    <code>{c.ccmSourceName}</code>
                                  </VDValue>
                                </VDRow>
                              )}
                            </VDSection>
                          )}

                          {isHost && (
                            <VDSection>
                              <VDSectionTitle>Host Package</VDSectionTitle>
                              <VDRow>
                                <VDLabel>Package Version</VDLabel>
                                <VDValue>
                                  <code>{c.hostPackageVersion || "—"}</code>
                                </VDValue>
                              </VDRow>
                            </VDSection>
                          )}

                          {isFirmware && (
                            <VDSection>
                              <VDSectionTitle>Firmware</VDSectionTitle>
                              <VDRow>
                                <VDLabel>FW Version</VDLabel>
                                <VDValue>
                                  <code>{c.firmwareVersion || "—"}</code>
                                </VDValue>
                              </VDRow>
                              <VDRow>
                                <VDLabel>S3 URI</VDLabel>
                                <VDValue>
                                  <code style={{ fontSize: 10 }}>
                                    {c.s3Uri || "—"}
                                  </code>
                                </VDValue>
                              </VDRow>
                              <VDRow>
                                <VDLabel>SHA-256</VDLabel>
                                <VDValue>
                                  <code
                                    style={{
                                      fontSize: 10,
                                      color: "#059669",
                                    }}
                                  >
                                    {c.sha256Hash || "—"}
                                  </code>
                                </VDValue>
                              </VDRow>
                            </VDSection>
                          )}

                          {(c.tags?.length > 0 || c.description) && (
                            <VDSection>
                              <VDSectionTitle>Notes &amp; Tags</VDSectionTitle>
                              {c.tags?.length > 0 && (
                                <VDRow>
                                  <VDLabel>Tags</VDLabel>
                                  <VDValue>
                                    <span
                                      style={{
                                        display: "flex",
                                        gap: 4,
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      {c.tags.map((tag) => (
                                        <span
                                          key={tag}
                                          style={{
                                            fontSize: 10,
                                            fontWeight: 600,
                                            padding: "1px 6px",
                                            borderRadius: 4,
                                            background: "#e0f2fe",
                                            color: "#0369a1",
                                          }}
                                        >
                                          {tag}
                                        </span>
                                      ))}
                                    </span>
                                  </VDValue>
                                </VDRow>
                              )}
                              {c.description && (
                                <VDRow>
                                  <VDLabel>Description</VDLabel>
                                  <VDValue>{c.description}</VDValue>
                                </VDRow>
                              )}
                            </VDSection>
                          )}
                        </CardBody>
                      </ComponentCard>
                    );
                  })}
                </ComponentsGrid>
              )}
            </ModalBody>
          </ModalBox>
        </ModalOverlay>
      )}
    </Container>
  );
});
